import { $ } from '../utils.js';
import { CONFIG } from '../config.js';
import { CursorAnimation } from './cursor.js';
import { MarqueeModule } from './marquee.js';

export const HeroAnimation = {
  elements: {},
  timeline: null,
  
  init() {
    this.cacheElements();
    if (this.elements.hero) {
      this.setupHeadingAnimation();
      this.setupInitialStates();
      this.createTimeline();
      this.observeHero();
    }
  },
  
  cacheElements() {
    this.elements = {
      hero: $(CONFIG.selectors.heroSection),
      heading: $(CONFIG.selectors.heading),
      description: $(CONFIG.selectors.description),
      luckyCard: $(CONFIG.selectors.luckyCard),
      image: $(CONFIG.selectors.image)
    };
  },
  
  setupHeadingAnimation() {
    const { heading } = this.elements;
    if (!heading) return;
    
    const words = (heading.textContent?.trim() || '').split(' ');
    heading.innerHTML = words.map((word, index) => {
      const chars = [...word].map(char => 
        `<span class="char">${char}</span>`
      ).join('');
      
      const space = index < words.length - 1 ? 
        '<span class="char-space"> </span>' : '';
      
      return `<span class="word">${chars}</span>${space}`;
    }).join('');
    
    this.applyGradientToChars(heading);
  },
  
  applyGradientToChars(heading) {
    const gradient = window.getComputedStyle(heading).backgroundImage;
    const headingRect = heading.getBoundingClientRect();
    const headingWidth = headingRect.width;
    
    heading.querySelectorAll('.char').forEach(char => {
      const charRect = char.getBoundingClientRect();
      const offset = charRect.left - headingRect.left;
      
      Object.assign(char.style, {
        backgroundImage: gradient,
        backgroundSize: `${headingWidth}px auto`,
        backgroundPosition: `-${offset}px 0px`,
        webkitBackgroundClip: 'text',
        backgroundClip: 'text',
        webkitTextFillColor: 'transparent',
        color: 'transparent',
        display: 'inline-block'
      });
    });
  },
  
  setupInitialStates() {
    const { heading, description, luckyCard, image } = this.elements;
    
    if (typeof window.gsap === 'undefined') return;
    
    if (heading) {
      heading.style.contain = 'layout style';
      heading.style.willChange = 'transform';
      window.gsap.set('.heading-style-h1 .char', {
        opacity: 0,
        y: 30,
        filter: 'blur(8px)',
        scale: 0.95
      });
    }
    
    if (description) {
      description.style.contain = 'layout style';
      description.style.willChange = 'transform, opacity, filter';
      window.gsap.set(description, {
        opacity: 0,
        y: 0,
        filter: 'blur(6px)'
      });
    }
    
    if (luckyCard) {
      luckyCard.style.contain = 'layout style';
      luckyCard.style.willChange = 'transform, opacity';
      window.gsap.set(luckyCard, {
        opacity: 0,
        y: 3.5,
        scale: 1
      });
    }
    
    if (image) {
      image.style.contain = 'layout style';
      image.style.willChange = 'transform, opacity';
      window.gsap.set(image, {
        opacity: 0,
        scale: 0.95
      });
    }
    
    // Set cursor initial opacity
    const cursors = document.querySelectorAll('.home_hero .cursor');
    cursors.forEach(cursor => {
      cursor.style.opacity = '0';
    });
  },
  
  createTimeline() {
    if (typeof window.gsap === 'undefined') return;
    
    this.timeline = window.gsap.timeline({
      paused: true,
      defaults: { ease: 'power3.out' },
      onComplete: () => this.cleanupPerformanceHints()
    });
    
    const { heading, description, luckyCard, image } = this.elements;
    
    if (heading) {
      this.timeline
        .addLabel('headline_in', 0)
        .to('.heading-style-h1 .char', {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          scale: 1,
          duration: 1,
          stagger: {
            each: 0.015,
            from: 'start'
          }
        }, 'headline_in');
    }
    
    if (description) {
      this.timeline
        .addLabel('description_in', 'headline_in+=0.75')
        .to(description, {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 2
        }, 'description_in');
    }
    
    if (luckyCard) {
      this.timeline.addLabel('box_in', 'description_in+=0');
      
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timeline.add(() => CursorAnimation.runTapStagger(), 'box_in');
      }
      
      this.timeline.to(luckyCard, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.75,
        ease: 'sine.inOut'
      }, 'box_in');
    }
    
    if (image) {
      this.timeline
        .addLabel('image207_in', 'box_in+=0.35')
        .to(image, {
          opacity: 1,
          scale: 1,
          duration: 1.5,
          ease: 'sine.inOut'
        }, 'image207_in');
    }
    
    this.timeline.add(() => {
      const resumeCarousel = () => {
        // Resume the carousel that was started paused
        MarqueeModule.resumeAll();
      };
      
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        resumeCarousel();
      } else {
        CursorAnimation.runCursorEntrance(() => {
          CursorAnimation.enableMotion();
          resumeCarousel();
        });
      }
    }, 'box_in+=0');
  },
  
  observeHero() {
    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (entry.isIntersecting) {
          obs.disconnect();
          if (this.timeline) {
            this.timeline.play();
          }
        }
      },
      { threshold: CONFIG.timing.heroThreshold }
    );
    
    observer.observe(this.elements.hero);
  },
  
  cleanupPerformanceHints() {
    const { heading, description, luckyCard, image } = this.elements;
    
    [heading, description, luckyCard, image].forEach(element => {
      if (element) {
        element.style.willChange = 'auto';
      }
    });
    
    const chars = document.querySelectorAll('.heading-style-h1 .char');
    chars.forEach(char => {
      char.style.willChange = 'auto';
    });
  }
};